import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useApiRequest from "../../lib/hooks/useRequest";
import { Dialog, DialogContent } from "../../ui/components/ui/dialog";
import { FeatureHintAnsicht, FeatureHintInhalt } from "./FeatureHintAnsicht";
import { useFeatureHintsContext } from "./FeatureHintsContext";

interface FeatureHint extends FeatureHintInhalt {
  key: string;
}

/**
 * Der Hinweis-Stapel nach der Anmeldung (KAN-241, KAN-252).
 *
 * Das Backend liefert bis zu drei ungelesene Hinweise, neueste zuerst. Bei einem Hinweis
 * sieht der Dialog aus wie zuvor; ab zwei kommen Standanzeige, "Zurueck", "Weiter" und
 * "Alle ueberspringen" dazu (A1, A3, A4, A6).
 *
 * Markiert wird ueber den gemeinsamen Stand, nicht per eigenem Aufruf: Sonst weiss die
 * Seitenleiste nichts davon und zeigt ihr Abzeichen weiter. Jeder Weg heraus markiert alles,
 * was der Stapel gezeigt haette - sonst stuende derselbe Stapel beim naechsten Anmelden
 * wieder da (A5).
 */
export const FeatureHintDialog = () => {
  const { apiRequest } = useApiRequest();
  const { markRead, melden } = useFeatureHintsContext();
  const navigate = useNavigate();
  const [hints, setHints] = useState<FeatureHint[]>([]);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const laden = async () => {
      const response = await apiRequest<{ hints: FeatureHint[] }>("feature-hints/dialog", "GET");
      // Nur etwas, das wirklich wie eine Folge von Hinweisen aussieht, wird eine. Ein blosses
      // "ist gesetzt" reicht nicht: Scheitert das Lesen der Antwort, reicht useRequest den
      // Platzhalter "success" durch - und der stand frueher als leerer Dialog auf dem Schirm.
      const geliefert = response.data?.hints;
      if (cancelled || !Array.isArray(geliefert)) return;
      setHints(geliefert.filter(hint => hint && typeof hint === "object" && "key" in hint));
    };

    laden();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Die Karte im Fenster ist die Vollansicht: Wer sie vor sich hat, hat sie gesehen und
  // geoeffnet zugleich (KAN-296). Gemeldet wird je Karte, nicht je Stapel - wer nach der
  // ersten wegklickt, hat die zweite nicht gesehen.
  useEffect(() => {
    const karte = hints[Math.min(index, hints.length - 1)];
    if (!karte) return;
    melden([
      { key: karte.key, art: "gesehen" },
      { key: karte.key, art: "geoeffnet" },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hints, index]);

  const schliessen = useCallback(() => {
    hints.forEach(hint => markRead(hint.key));
    setHints([]);
    setIndex(0);
  }, [hints, markRead]);

  // Erst nach allen Haken: Ohne diese Zeile stuende ein leeres Fenster auf dem Schirm.
  if (hints.length === 0) return null;

  const aktuell = hints[Math.min(index, hints.length - 1)];
  const istLetzte = index >= hints.length - 1;
  const mehrereKarten = hints.length > 1;

  const weiter = () => {
    if (istLetzte) {
      schliessen();
      return;
    }
    markRead(aktuell.key);
    setIndex(index + 1);
  };

  // Zurueck blaettert nur; gelesen bleibt gelesen. Wer eine Karte zu schnell weggeklickt
  // hat, soll sie wiederfinden, ohne den Stapel neu auszuloesen.
  const zurueck = () => setIndex(Math.max(0, index - 1));

  const zurFunktion = (ziel: string) => {
    schliessen();
    // Der Hinweis fuehrt auf die Seite der Funktion, nicht in den Terminkalender: Dort steht
    // die Terminbuchung ohnehin, und der Kunde liest vorher, worum es geht.
    navigate(ziel);
  };

  return (
    <Dialog open onOpenChange={offen => !offen && schliessen()}>
      {/* Nur der Inhalt scrollt; die Bedienleiste bleibt unten stehen. Mit Bild, Video und
          zwei Absaetzen muesste man sonst erst ans Ende scrollen, um weiterzukommen (A12). */}
      {/* `flex flex-col` ueberschreibt das Raster, das der Dialog sonst mitbringt: Darin
          wirken weder `flex-1` am Inhalt noch `shrink-0` an der Leiste, der Inhalt nimmt
          seine volle Hoehe und schiebt die Bedienleiste unten aus dem Fenster heraus. */}
      <DialogContent className="flex max-h-[85vh] flex-col gap-0 overflow-hidden p-0">
        <div className="flex-1 overflow-y-auto p-6">
          <FeatureHintAnsicht inhalt={aktuell} onInternal={zurFunktion} onGriff={(art, stelle) => melden([{ key: aktuell.key, art, stelle }])} />
        </div>

        {mehrereKarten && (
          <div className="flex shrink-0 items-center justify-between gap-3 border-t border-gray-200 bg-white px-6 py-3">
            <div className="flex items-center gap-2" role="img" aria-label={`Hinweis ${index + 1} von ${hints.length}`}>
              {hints.map((hint, nummer) => (
                <span
                  key={hint.key}
                  className={`block h-2 rounded-full ${nummer === index ? "w-[22px] bg-neuigkeit" : "w-2 bg-slate-300"}`}
                />
              ))}
            </div>
            <div className="flex items-center gap-2">
              <button type="button" onClick={schliessen} className="mr-1 text-sm font-medium text-gray-500">
                Alle überspringen
              </button>
              {index > 0 && (
                <button
                  type="button"
                  onClick={zurueck}
                  className="min-h-10 rounded-lg border border-gray-300 px-4 text-sm font-semibold text-gray-700"
                >
                  Zurück
                </button>
              )}
              <button
                type="button"
                onClick={weiter}
                className="min-h-10 rounded-lg border border-gray-300 px-4 text-sm font-semibold text-gray-700"
              >
                {istLetzte ? "Fertig" : "Weiter"}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FeatureHintDialog;
